import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { LevelAsset, AssetsDataService, ILevelAsset } from '@services/assets-data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { distinctUntilChanged, filter, finalize, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, interval, Subject, Subscription } from 'rxjs';
import { EditHierarchyLocationComponent } from '../../../dialogs/edit-hierarchy-location/edit-hierarchy-location.component';
import { MatDialog } from '@angular/material/dialog';
import { IOrganisationsHierarchy, OrganisationDataService } from '@services/organisation-data.service';

/**
 * Component
 */
@Component({
	selector: 'app-asset-tiles-group',
	templateUrl: './asset-tiles-group.component.html',
	styleUrls: ['./asset-tiles-group.component.scss']
})
export class AssetTilesGroupComponent implements OnInit, OnDestroy, AfterViewInit {

	/** Path to Sub Location Images */
	public imagePathSubLocations(): string {
		return imagePath(this.accountGUID, imgTypes.sublocations).toLowerCase()
	}

	public imagePathOrganisations(): string {
		return imagePath(this.accountGUID, imgTypes.organisations).toLowerCase()
	}

	/** inputSubLocation */
	@Input() inputSubLocation: IOrganisationsHierarchy;

	/** Site admin */
	@Input() AdminSite: boolean;

	/** Assets List */
	public locationAssets: LevelAsset[] = [];

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountGUID */
	public accountGUID: string

	/** OrganisationUID */
	public OrganisationUID: string

	/** Editing the Sensor Tiles */
	public sensorEdit: boolean = false

	/** selected */
	@Output() selected: EventEmitter<ILevelAsset> = new EventEmitter<ILevelAsset>();

	/** selectedAsset */
	public selectedAsset?: ILevelAsset

	@ViewChild('image', { static: false }) imageElement!: ElementRef<HTMLImageElement>;

	public top: number;
	public left: number;
	public width: number;
	public height: number;

	public textSize: number;
	public iconSize: number;

	public showWatchIcons: boolean = true;

	private resizeObserver!: ResizeObserver;

	public isMobileView: boolean = false;

	private routerSubscription!: Subscription;

	/** startingWidth */
	public startingWidth: string = "100%";

	/** startingHeight */
	public startingHeight: string = "calc(100% - 20px)";

	/** componentWidth */
	public componentWidth: string = this.startingWidth;

	/** componentHeight */
	public componentHeight: string = this.startingHeight;

	/** isFullScreen */
	public isFullScreen: boolean = false;

	/** hideButtons */
	@Output() hideButtons = new EventEmitter<boolean>();

	/** Observable Subject Finalise */
	private tileFinalise = new Subject<void>();

	private blnRender: boolean = false
	/**
   *  Default Constructor
   * @param organisationDataService Organisation Data
   * @param assetsDataService AssetsDataService
   * @param router router
   * @param route route
   * @param siteSubscriptions site Subscriptions
   * @param globalFunctions global Functions
   * @param dialog dialog
   */
	constructor(
		public organisationDataService: OrganisationDataService,
		public assetsDataService: AssetsDataService,
		private router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		public dialog: MatDialog,
	) {
		try {
			combineLatest([
				this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([selectedAccountGUID, selectedOrganisationUID]) => !!selectedAccountGUID && !!selectedOrganisationUID)
			).subscribe({
				next: ([selectedAccountGUID, selectedOrganisationUID]) => {
					this.accountGUID = selectedAccountGUID;
					this.OrganisationUID = selectedOrganisationUID;
				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});

			this.siteSubscriptions.selectedAsset$.pipe(
				tap(() => {
					// Perform actions before the observable emits anything
					this.selectedAsset = undefined
				}),
				takeUntil(this.finalise), distinctUntilChanged(),
				filter((asset) => !!asset)
			).subscribe({
				next: (asset_s: any) => {
					try {
						let asset: LevelAsset;
						if (typeof (asset_s) == 'string') {
							asset = JSON.parse(asset_s)
							this.selectedAsset = asset
						} else {
							this.selectedAsset = asset_s
						}
					} catch (error) { this.globalFunctions.HandleError(error, "") }
				},
				error: (error) => { }
			});
		}
		catch (error) { }
	}

	ngAfterViewInit() {

		let refreshTime = 0.5;
		let refreshInterval = interval((refreshTime * 1000)).pipe(takeUntil(this.tileFinalise)).subscribe(() => {
			if (this.blnRender) {
				this.getImagePosition(); // Recalculate position on resize
				// this.scaleTextSize();
			}
			this.blnRender = false;
		});

		// Set up Resize Observer
		this.resizeObserver = new ResizeObserver(() => {
			this.blnRender = true;
		});

		// Observe the image element
		if (this.imageElement) {
			this.resizeObserver.observe(this.imageElement.nativeElement);
		}

		const userAgent = window.navigator.userAgent;
		// Basic device type check based on user agent
		this.isMobileView = /Mobi|Android|iPhone|iPad|iPod/.test(userAgent);

		document.addEventListener("fullscreenchange", () => {
			if (document.fullscreenElement) {
				this.isFullScreen = true;
				this.hideButtons.emit(true);
			} else {
				this.isFullScreen = false;
				this.hideButtons.emit(false);
			}
		});
	}

	getImagePosition() {
		if (this.imageElement) {
			const rect = this.imageElement.nativeElement.getBoundingClientRect();
			const parentElement = this.imageElement.nativeElement.parentElement;

			// Get the bounding rect of the parent element
			const parentRect = parentElement?.getBoundingClientRect();

			if (parentRect) {
				// Calculate the position relative to the parent
				this.top = rect.top - parentRect.top;
				this.left = rect.left - parentRect.left;
				this.width = rect.width;
				this.height = rect.height;

				let area = this.width * this.height;

				if (area <= 65000) {
					this.showWatchIcons = false;
				} else {
					this.showWatchIcons = true;
				}				
			} else {
				console.error('Parent element not found.');
			}
		} else {
			console.error('Image element not found.');
		}
	}

	// scaleTextSize() {
	// 	if (this.width && this.height) {
	// 		if (this.width > 650) {
	// 			this.textSize = this.height / 50;
	// 		} else if (this.width < 650 && this.width > 300) {
	// 			this.textSize = this.height / 35;
	// 		} else {
	// 			this.textSize = this.height / 30;
	// 		}
	// 		this.textSize = 10;
	// 		this.iconSize = this.textSize * 1.6
	// 	}
	// }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void { }

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();

			this.tileFinalise.next();
			this.tileFinalise.complete();

			if (this.resizeObserver) {
				this.resizeObserver.unobserve(this.imageElement.nativeElement);
			}
		}
		catch { }

	}

	/**
	 * Initilise the movement of sensors
	 */
	moveSensor() {

		const assetObjects = document.querySelectorAll('.vib-sensor');

		/** Editing the Sensors */
		this.sensorEdit = true

		assetObjects.forEach((asset) => {
			let isDragging = false;
			let initialMouseX: number, initialMouseY: number, initialSensorX: number, initialSensorY: number;

			function handleMouseDown(e: any) {
				isDragging = true;

				initialMouseX = e.clientX;
				initialMouseY = e.clientY;

				let sensorRect = asset.getBoundingClientRect();
				// let parentNode = asset.parentElement?.getBoundingClientRect();

				let locationimage = document.getElementById("locationimage");
				if (locationimage) {
					// let containerRect = asset.parentElement?.getBoundingClientRect();
					// Defaulting in the case of null
					let parentNode = locationimage.getBoundingClientRect();

					if (parentNode != null) {
						initialSensorX = sensorRect.left - parentNode.left;
						initialSensorY = sensorRect.top - parentNode.top;
					}
					asset.classList.add('dragging');

					let tmpSensor: any = asset
					tmpSensor.style.cursor = "pointer";
					asset = tmpSensor;
				}
			}

			function handleMouseMove(e: { clientX: any; clientY: any; }) {
				if (!isDragging) return;


				let locationimage = document.getElementById("locationimage");
				if (locationimage) {
					// let containerRect = asset.parentElement?.getBoundingClientRect();
					// Defaulting in the case of null
					let containerRect = locationimage.getBoundingClientRect();
					let containerWidth = 0;
					let containerHeight = 0;
					if (containerRect != null) {
						containerWidth = containerRect.width;
						containerHeight = containerRect.height;
					}
					let tmpSensor: any = asset

					let sensorWidth = tmpSensor.offsetWidth;
					let sensorHeight = tmpSensor.offsetHeight;

					let newSensorX = initialSensorX + e.clientX - initialMouseX;
					let newSensorY = initialSensorY + e.clientY - initialMouseY;

					if (newSensorX < 0) {
						newSensorX = 0;
					} else if (newSensorX + sensorWidth > containerWidth) {
						newSensorX = containerWidth - sensorWidth;
					}

					if (newSensorY < 0) {
						newSensorY = 0;
					} else if (newSensorY + sensorHeight > containerHeight) {
						newSensorY = containerHeight - sensorHeight;
					}

					tmpSensor.style.left = `${(newSensorX / containerWidth) * 100}%`;
					tmpSensor.style.top = `${(newSensorY / containerHeight) * 100}%`;

					asset = tmpSensor;
				}
			}

			function handleMouseUp(e: any) {
				if (!isDragging) return;

				asset.classList.remove('handler');
				asset.classList.remove('dragging');

				isDragging = false;

				// save the new position to the SQL database here
			}

			function handleRemove() {

				let saveButton = document.getElementById("save-btn");
				let cancelButton = document.getElementById("cancel-btn");
				const assetObjects = document.querySelectorAll('.vib-sensor');
				assetObjects.forEach((asset) => {

					let sensorRect = asset.getBoundingClientRect();
					// let parentNode = asset.parentElement?.getBoundingClientRect();

					let locationimage = document.getElementById("locationimage");
					if (locationimage) {
						// let containerRect = asset.parentElement?.getBoundingClientRect();
						// Defaulting in the case of null
						let parentNode = locationimage.getBoundingClientRect();

						let leftPercent = 0;
						let topPercent = 0;

						if (parentNode != null) {
							initialSensorX = sensorRect.left - parentNode.left;
							initialSensorY = sensorRect.top - parentNode.top;
							leftPercent = (initialSensorX / parentNode.width * 100);
							topPercent = (initialSensorY / parentNode.height * 100);
						}

						if (asset.classList.contains('handlerAdded')) {
							/** Update the SQL with new position */

						}
						asset.removeEventListener("mousedown", handleMouseDown);
						document.removeEventListener("mousemove", handleMouseMove);
						document.removeEventListener("mouseup", handleMouseUp);

						let tmpSensor: any = asset
						tmpSensor.style.cursor = "default";
						asset = tmpSensor;

						// console.log(asset.id, leftPercent, topPercent)

						asset.classList.remove('handlerAdded');

						if (cancelButton != null) {
							cancelButton.removeEventListener("click", handleRemove);
						}

						if (saveButton != null) {
							saveButton.removeEventListener("click", handleRemove);
						}
					}
				});
			}

			if (!asset.classList.contains('handlerAdded')) {
				asset.addEventListener('mousedown', handleMouseDown);
				document.addEventListener('mousemove', handleMouseMove);
				document.addEventListener('mouseup', handleMouseUp);

				let sensorRect = asset.getBoundingClientRect();
				// let parentNode = asset.parentElement?.getBoundingClientRect();

				let locationimage = document.getElementById("locationimage");
				if (locationimage) {
					// let containerRect = asset.parentElement?.getBoundingClientRect();
					// Defaulting in the case of null
					let parentNode = locationimage.getBoundingClientRect();

					let leftPercent = 0;
					let topPercent = 0;

					if (parentNode != null) {
						initialSensorX = sensorRect.left - parentNode.left;
						initialSensorY = sensorRect.top - parentNode.top;
						leftPercent = (initialSensorX / parentNode.width * 100);
						topPercent = (initialSensorY / parentNode.height * 100);
					}

					// console.log(asset.id, leftPercent, topPercent)

					let tmpSensor: any = asset
					tmpSensor.style.cursor = "pointer";
					asset = tmpSensor;

					asset.classList.add('handlerAdded');

					let saveButton = document.getElementById("save-btn");
					saveButton?.addEventListener('click', handleRemove);

					let cancelButton = document.getElementById("cancel-btn");
					cancelButton?.addEventListener('click', handleRemove);
				}
			}

		});
	}

	/** counter used to ensure hierarchy only set once */
	private counter: number;

	/** number of asset tiles */
	private assetTilesCount: number;

	/**
	 * Save the position of the Sensor Tiles
	 */
	savePositions(save: boolean) {
		this.counter = 0; // ensure counter is at 0
		this.assetTilesCount = this.inputSubLocation.Assets.filter((x: LevelAsset) => x.DisplayOnImage).length; //make sure we only count displayed assets
		if (!this.sensorEdit) { return }
		const assetObjects = document.querySelectorAll('.vib-sensor');
		assetObjects.forEach((asset) => {
			if (asset.classList.contains('handlerAdded')) {

				asset.classList.remove('handlerAdded');
				asset.classList.remove('dragging');

				let sensorRect = asset.getBoundingClientRect();

				let parentNodes = [];
				parentNodes.push(asset.parentElement);
				parentNodes.push(asset.parentElement?.parentElement);

				let parentNode = parentNodes.find((x: any) => x.classList.contains("tiles-canvas"))?.getBoundingClientRect();

				// let parentNode = asset.parentElement?.getBoundingClientRect();

				if (parentNode != null) {
					let initialSensorX = sensorRect.left - parentNode.left;
					let initialSensorY = sensorRect.top - parentNode.top;

					// let leftPercent = Math.round(initialSensorX / parentNode.width * 100);
					// let topPercent = Math.round(initialSensorY / parentNode.height * 100);

					let leftPercent = (initialSensorX / parentNode.width * 100);
					let topPercent = (initialSensorY / parentNode.height * 100);

					var myAsset = this.inputSubLocation.Assets.find((x: LevelAsset) => x.AssetID.toString() == asset.id)
					if (myAsset && save) {
						myAsset.LeftPosition = leftPercent;
						myAsset.TopPosition = topPercent;
					}
					this.updateTilePosition(this.accountGUID, this.OrganisationUID, myAsset);
				}
			}
		});

		/** Stopped Editing the Sensors */
		this.sensorEdit = false
	}

	/**
	 * Update System Tile information
	 * @param systemID
	 * @param groupID
	 * @param systemSensorID
	 * @param topPosition
	 * @param leftPosition
	 */
	updateTilePosition(accountGUID: string, organisationUID: string, asset: any) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.patchOrganisationAssets$(accountGUID, organisationUID, asset).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (response) => {
				try {
					this.counter++; // increment counter for each successful tile position update
					if (this.counter == this.assetTilesCount) { // only set hierarchy after the final tile update
						this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID);
					}
				}
				catch (error) { this.globalFunctions.HandleError(error, "") }
			},
			error: (error) => {
				console.error(error)
				this.globalFunctions.HandleError(error, "updateSystemsGroups: Error updating system group info")
			},
			complete: () => { }
		});
	}

	/**
	 * Used to catch any tile positions that are less then zero
	 * @param position
	 * @returns
	 */
	getSafePosition(position: number): number {
		return position < 0 ? 0 : position;
	}

	/**
  * Get the Organisation Structure
  * @param AccountID AccountID
  * @param OrganisationID OrganisationID
  */
	subscribeOrganisationStructure(accountGUID: string, organisationUID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			/** Get the Organisation for the selection */
			this.organisationDataService.getOrganisationStructure$(accountGUID, organisationUID).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((structure) => !!structure),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (structure: any) => {
					try {
						/** Update the Organisation */
						var OrganisationStructure = <IOrganisationsHierarchy[]>JSON.parse(structure);
						this.siteSubscriptions.SethierarchicalData(OrganisationStructure);
					} catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
					}
				},
				//complete: () => { console.log("OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe: Complete"); }
			});
		} catch (error) { console.error(error); }
	}

	/**
	 * Bind the Select event
	 * @param asset
	 */
	public assetSelect(asset: ILevelAsset) {
		if (this.sensorEdit) { return; }

		try {
			if (asset != this.selectedAsset) {
				this.selectedAsset = asset;
				this.siteSubscriptions.SetSelectedAsset(asset);
			}
		} catch (error) { console.error(error); }
	}
	
	/**
	 * Edit the Location details
	 * @param editLocation
	 */
	editLocation(editLocation: IOrganisationsHierarchy): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Location Information
		var _edit = { ...editLocation };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyLocationComponent, {
			data: _edit,
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			this.organisationDataService.patchOrganisationLocation$(this.accountGUID, this.OrganisationUID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					Object.assign(editLocation, result);
					this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID)
					this.globalFunctions.DisplaySnackBar({
						message: "Location Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
				},
				error: (error) => { this.globalFunctions.HandleError(error, "asset-tiles Error: this.organisationDataService.patchOrganisationLocation$", false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}
	
	/**
	 * trueFullscreen
	 * @param element element  
	 */
	trueFullscreen(element: HTMLElement, fullscreen: boolean) {
		let elements: HTMLElement[] = [element, element.parentElement!, element.parentElement?.parentElement!];
		let targetElement = elements.find((x: HTMLElement) => x.classList.contains("leftcont"));
		if (targetElement) {
			if (fullscreen) { targetElement.requestFullscreen(); }
			else { document.exitFullscreen(); }
		}
	}
}
